import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import type { TimeCardShift } from '../../../../../js/api';
import { Button } from '../../../components/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogCloseButton,
  DialogTitle,
} from '../../../components/dialog';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { formatDateTimeRange } from '../../../utils/date';
import { reactSelectReset } from '../../../utils/utils';

export const ShiftRemoveModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { removal_reason: string; explanation: string }) => void;
  shift?: TimeCardShift;
  isPending?: boolean;
}> = ({ open, onClose, onConfirm, shift, isPending = false }) => {
  const { data: reasonOptions } = useCodeDefinitionsAsOptions('RemovalReason');
  const activeReasonOptions = useMemo(
    () => reasonOptions.filter((o) => o.is_active),
    [reasonOptions]
  );
  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setExplanation('');
    }
  }, [open]);

  const handleSubmit = () => {
    onConfirm({ removal_reason: reason, explanation });
  };

  const shiftDate = shift ? formatDateTimeRange(shift) : '';

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <div className="tw:flex tw:items-start tw:justify-between">
        <DialogTitle>Remove Shift</DialogTitle>
        <DialogCloseButton onClick={onClose} />
      </div>
      <DialogBody>
        <div className="tw:space-y-4">
          {shiftDate && (
            <div className="tw:text-sm tw:text-zinc-600">
              <i className="far fa-calendar tw:mr-1" />
              {shiftDate}
            </div>
          )}
          <div>
            <label
              htmlFor="removal-reason"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Reason <span className="tw:text-stoplight-danger">*</span>
            </label>
            <Select
              id="removal-reason"
              options={activeReasonOptions}
              value={activeReasonOptions.find((o) => o.value === reason) || null}
              onChange={(opt) => setReason(opt?.value || '')}
              placeholder="Select reason..."
              styles={reactSelectReset}
              isClearable={false}
            />
          </div>
          <div>
            <label
              htmlFor="removal-explanation"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Explanation <span className="tw:text-stoplight-danger">*</span>
            </label>
            <textarea
              id="removal-explanation"
              className="tw:w-full tw:rounded-lg tw:bg-white tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              rows={2}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
            />
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          color="stoplight-danger"
          onClick={handleSubmit}
          disabled={isPending || !reason || !explanation.trim()}
        >
          {isPending ? 'Removing...' : 'Remove Shift'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
